import React, { useEffect, useState, useContext } from "react"
import { useNavigate } from "react-router-dom"
import {
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Grid,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import ListAltIcon from "@mui/icons-material/ListAlt"
import CheckBoxTwoToneIcon from "@mui/icons-material/CheckBoxTwoTone"
import CheckBoxOutlineBlankTwoToneIcon from "@mui/icons-material/CheckBoxOutlineBlankTwoTone"
import ChatTwoToneIcon from "@mui/icons-material/ChatTwoTone"
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone"
import BookDialog from "./BookDialog"
import ContactFormDialog from "../../../components/ContactFormDialog"
import UserContext from "../../../context/UserContext"
import useFetch from "../../../hooks/useFetch"
import useMutation from "../../../hooks/useMutation"
import groupBooksByGrade from "../../../functions/groupBooksByGrade"
import { F_TRADING_ACTIVE } from "../../../constants/routes"
import BackdropContext from "../../../context/BackdropContext"
import StateContext from "../../../context/StateContext"

const CustomPaper = (props) => <Paper elevation={0} {...props} />

const Search = ({ isMobile, setSnackbarMsg }) => {
  const navigate = useNavigate()
  const { backdropOpen, openBackdrop, closeBackdrop } = useContext(BackdropContext)
  const { userData } = useContext(UserContext)
  const schoolID = userData.user.schoolId
  const userId = userData.user.id

  const [currentClass, setCurrentClass] = useState(5)
  const [currentBook, setCurrentBook] = useState(null)
  const [contactFormOpen, setContactFormOpen] = useState(false)

  const { ledgerList, ledgerLoading, ledgerRefetch } = useContext(StateContext)

  const { data: bookList, loading: booksLoading } = useFetch(`/school/${schoolID}/books`, { cache: true })
  const booksByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => !b.isBundle))
  const bookBundlesByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => b.isBundle))

  const { mutate: buyOrSellMutation, loading: buyOrSellLoading } = useMutation("/ledger")

  const loading = booksLoading || ledgerLoading || buyOrSellLoading

  const refetch = () => {
    // booksRefetch()
    ledgerRefetch()
  }

  useEffect(() => {
    if (loading) {
      openBackdrop()
    } else {
      if (backdropOpen) {
        closeBackdrop()
      }
    }
  }, [loading, openBackdrop, backdropOpen, closeBackdrop])

  const getBookStatus = (row) => {
    const defaultTooltip = (
      <Tooltip arrow placement="top" title="Nicht eingestellt">
        <CheckBoxOutlineBlankTwoToneIcon />
      </Tooltip>
    )

    if (loading) {
      return defaultTooltip
    }

    const bookInLedger = ledgerList.find((e) => e.bookId === row.id)
    if (!bookInLedger) {
      return defaultTooltip
    }

    const statusConfig = {
      search: {
        title: "Partner wird gesucht...",
        Icon: SearchTwoToneIcon,
        sx: {},
      },
      matched: {
        title: "Partner gefunden",
        Icon: ChatTwoToneIcon,
        sx: { color: "primary.main" },
      },
      finished: {
        title: "Abgeschlossen",
        Icon: CheckBoxTwoToneIcon,
        sx: {},
      },
    }

    const config = statusConfig[bookInLedger.status]
    if (!config) {
      return defaultTooltip
    }

    const { title, Icon, sx } = config

    return (
      <Tooltip arrow placement="top" title={title}>
        <Icon sx={sx} />
      </Tooltip>
    )
  }

  const handleBuy = async () => {
    try {
      await buyOrSellMutation({
        userId,
        bookId: currentBook.id,
        action: "buy",
      })

      setSnackbarMsg("Buch zum Kauf eingestellt")
      navigate(F_TRADING_ACTIVE)
    } catch (e) {
      console.log(e.message)
    }
    setCurrentBook(null)
    refetch()
  }

  const handleSell = async () => {
    try {
      await buyOrSellMutation({
        userId,
        bookId: currentBook.id,
        action: "sell",
      })

      setSnackbarMsg("Buch zum Verkauf eingestellt")
      navigate(F_TRADING_ACTIVE)
    } catch (e) {
      console.log(e.message)
    }
    setCurrentBook(null)
    refetch()
  }

  const handleClickRow = (book) => {
    setCurrentBook(book)
  }

  const handleClose = () => {
    setCurrentBook(null)
  }

  const handleOpenBundle = () => {
    const currentBundle = bookList.filter((b) => b.grade === currentClass && b.isBundle)?.[0]
    if (!currentBundle) {
      return
    }
    setCurrentBook(currentBundle)
  }

  const rows = booksByGrade[currentClass] || []

  return (
    <>
      <Grid container justifyContent="space-between" sx={{ ml: "1vw", mt: "1vw", width: "98%" }}>
        <Grid item xs={isMobile ? 12 : undefined}>
          <Typography
            variant="h5"
            align={isMobile ? "center" : "left"}
            sx={{ color: "secondary.main", width: isMobile ? "98vw" : "auto", mb: isMobile ? 4 : 0 }}>
            {currentClass ? `Bücherliste Klasse ${currentClass}` : "Extrabücher"}
          </Typography>
        </Grid>

        <Grid item xs={isMobile ? 12 : undefined}>
          <Button
            sx={{ visibility: currentClass > 0 ? "visible" : "hidden", width: isMobile ? "90%" : "auto", mx: isMobile ? "5%" : "inherit" }}
            variant="outlined"
            disabled={!bookBundlesByGrade?.[currentClass]}
            color="secondary"
            startIcon={<ListAltIcon color={!bookBundlesByGrade?.[currentClass] ? "inherit" : "secondary"} />}
            onClick={handleOpenBundle}>
            Alle Bücher zusammen kaufen
          </Button>
        </Grid>
      </Grid>

      <Paper
        elevation={6}
        sx={{ width: isMobile ? "90%" : "calc(100% - 2vw)", mx: isMobile ? "5%" : "1vw", mt: isMobile ? 5 : "5vh", borderRadius: "20px" }}>
        <ButtonGroup
          variant="outlined"
          sx={{ width: "100%", height: "5vh", borderTopLeftRadius: "20px", borderTopRightRadius: "20px", overflow: "auto" }}>
          {[5, 6, 7, 8, 9, 10, 11, 12, 13, 0].map((c) => (
            <Button
              onClick={() => setCurrentClass(c)}
              fullWidth
              key={c}
              sx={{
                backgroundColor: c !== currentClass ? "primary.main" : "white",
                color: c === currentClass ? "secondary.main" : "secondary.main",
                "&:last-child": { borderTopRightRadius: "20px" },
                "&:first-child": { borderTopLeftRadius: "20px" },
              }}>
              {c ? c : isMobile ? "☆" : "Extra"}
            </Button>
          ))}
        </ButtonGroup>

        <TableContainer
          sx={{ borderBottomRightRadius: "20px", borderBottomLeftRadius: "20px", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
          component={CustomPaper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "5%" }}>
                  <Tooltip arrow title="Buch gekauft oder verkauft" placement="top">
                    <CheckBoxOutlineBlankTwoToneIcon />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: isMobile ? "95%" : "25%" }}>Titel</TableCell>
                {isMobile ? (
                  <></>
                ) : (
                  <>
                    <TableCell sx={{ width: "5%" }} align="right">
                      Fach
                    </TableCell>
                    <TableCell sx={{ width: "15%" }} align="right">
                      ISBN
                    </TableCell>
                    <TableCell sx={{ width: "15%" }} align="right">
                      Verlag
                    </TableCell>
                    <TableCell sx={{ width: "10%" }} align="right">
                      Originalpreis
                    </TableCell>
                    <TableCell sx={{ width: "25%" }} align="right">
                      Information
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  onClick={() => handleClickRow(row)}
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                  <TableCell component="th" scope="row">
                    {getBookStatus(row)}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.title}
                  </TableCell>
                  {isMobile ? (
                    <></>
                  ) : (
                    <>
                      <TableCell align="right">{row.subject}</TableCell>
                      {/* eslint-disable-next-line */}
                      <TableCell align="right">{!!row.isbn && row.isbn != 0 ? row.isbn : ""}</TableCell>
                      <TableCell align="right">{row.publisher}</TableCell>
                      <TableCell align="right">{row.priceOriginal ? `${row.priceOriginal} €` : ""}</TableCell>
                      <TableCell align="right">{row.comment}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography
        onClick={() => setContactFormOpen(true)}
        variant="caption"
        sx={{ mt: 2, mr: 2, display: "flex", justifyContent: "flex-end", cursor: "pointer" }}>
        Buch nicht gefunden?
      </Typography>

      {contactFormOpen && (
        <ContactFormDialog
          text="Du suchst ein Buch, welches hier nicht gelistet ist? Hier kannst du deine Anfrage senden:"
          placeholder="Hier fehlt noch Buch XY..."
          handleClose={() => setContactFormOpen(false)}
          onSend={() => setSnackbarMsg("Anfrage gesendet!")}
        />
      )}

      <BookDialog handleClose={handleClose} book={currentBook} buy={handleBuy} sell={handleSell} isMobile={isMobile} ledgerList={ledgerList} />
    </>
  )
}

export default Search
